import {
  AspectRatio,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { HSeparator, VSeparator } from "components/separator/Separator";
import { motion } from "framer-motion";
import React, { useMemo, useRef, useState } from "react";
import { FaAndroid, FaApple, FaCcApplePay, FaPlay } from "react-icons/fa";
import { FcAndroidOs } from "react-icons/fc";
import { NumberWithCommas } from "utils";
import {
  RoasGoal,
  useCampaignCreate,
} from "views/admin/campaign_create/roas/components/CampaignCreateContext";
import {
  GRADIENT_BUTTON_1,
  GRADIENT_BUTTON_2,
  GRADIENT_BUTTON_3,
  hexToRGB,
  PURPLE,
} from "views/admin/campaign_create/roas/components/Creatives";
import Chart from "react-apexcharts";
import GamelightButton from "components/button";
import { Post, Put, url } from "api";
import { useHistory } from "react-router-dom";
import { useQuery } from "hooks/useQuery";
import Lottie from "react-lottie";

import * as animationData from "../../../../../assets/animations/coin-animated.json";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "helper/GlobalLoadingDispatches";

const Preview = () => {
  const searchParams = useQuery();
  const campaignId = searchParams.get("id");

  const isCampaignCreate = !campaignId;

  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();
  const { campaign, creatives, roasGoals, creativeFiles } = useCampaignCreate();
  const canEditCampaign = isCampaignCreate ? true : (campaign as any)?.isDirect ?? true;
  const { onOpen, onClose, isOpen } = useDisclosure();

  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlayVideo = () => {
    onOpen();
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleCloseModal = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    onClose();
  };

  const platform = useMemo(
    () => (campaign?.platform === "1" ? "Android" : "iOS"),
    [campaign?.platform]
  );

  const onSubmit = async () => {
    setIsSubmitting(true);

    try {
      showGlobalLoading("Creating campaign");
      const response = !campaign?.id
        ? await Post("/dashboard/campaigns", {
            ...campaign,
            platform: campaign.platform === "1" ? "android" : "ios",
          })
        : await Put(`/dashboard/campaigns/${campaign?.id}`, campaign);

      if (response.err) {
        toast({
          title: "Error",
          description:
            "An error occured while creating/updating campaign. Error: " +
            response.err,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const campaignId = !campaign?.id ? response.id : campaign?.id;

      const _roasData: { [key: string]: number } = {};

      for (const [key, value] of Object.entries(roasGoals)) {
        const newKey = key.toUpperCase();
        const newValue = parseFloat(value) / 100;
        _roasData[newKey] = newValue;
      }

      showGlobalLoading("Entering ROAS goals");
      const roasResponse = await Put(
        `/dashboard/campaigns/${campaignId}/goals`,
        _roasData
      );

      if (roasResponse.err) {
        toast({
          title: "Error",
          description:
            "An error occured while entering ROAS goals. Error: " +
            response.err,
          status: "error",
          duration: 5000,
        });
      }

      if (creativeFiles.bannerImage || creativeFiles.bannerVideo) {
        if (creativeFiles.bannerImage) {
          const arrayBuffer = await creativeFiles.bannerImage.arrayBuffer();
          const myHeaders = new Headers();
          myHeaders.append(
            "Content-Disposition",
            `attachment;filename=banner.png`
          );
          myHeaders.append("Content-Type", "image/png");
          myHeaders.append(
            "Authorization",
            "Bearer " + localStorage.getItem("jwtToken")
          );

          showGlobalLoading("Uploading banner image");
          const response = await fetch(
            `${url}/dashboard/campaigns/${campaignId}/creatives/banner`,
            {
              method: "PUT",
              body: arrayBuffer,
              headers: myHeaders,
            }
          );

          const json = await response
            .clone()
            .json()
            .catch((e) => response.text());

          if (json.err) {
            toast({
              title: "Error",
              description:
                "An error occured while uploading banner image. Error: " +
                json.err,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }

        if (creativeFiles.bannerVideo) {
          const arrayBuffer = await creativeFiles.bannerVideo.arrayBuffer();
          const myHeaders = new Headers();
          myHeaders.append(
            "Content-Disposition",
            `attachment;filename=video.mp4`
          );
          myHeaders.append("Content-Type", "video/mp4");
          myHeaders.append(
            "Authorization",
            "Bearer " + localStorage.getItem("jwtToken")
          );
          showGlobalLoading("Uploading video");
          const response = await fetch(
            `${url}/dashboard/campaigns/${campaignId}/creatives/video`,
            {
              method: "PUT",
              body: arrayBuffer,
              headers: myHeaders,
            }
          );

          const json = await response
            .clone()
            .json()
            .catch((e) => response.text());

          if (json.err) {
            toast({
              title: "Error",
              description:
                "An error occured while uploading video. Error: " + json.err,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }
      }

      toast({
        title: campaign?.id
          ? "Campaign updated successfully."
          : "Campaign created successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      history.push("/ua/roas");
    } catch (error: any) {
      console.error(error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      hideGlobalLoading();
    }
  };

  return (
    <Flex flex={1} flexDir="column" mb={"10vh"}>
      <Flex flexDir="column" padding={4}>
        <Flex flex={1} px={4} gap={8}>
          <Flex flexDir={"column"} gap={2} flex={1}>
            <Flex
              flexDir={"column"}
              gap={1}
              bgColor="white"
              p={4}
              borderRadius={"lg"}
              minHeight={452}
            >
              <Heading mb={8} fontSize={"2xl"} color="gray.700">
                Campaign Details
              </Heading>
              <Flex gap={2}>
                <Text color="gray.700" fontWeight={"semibold"} fontSize="lg">
                  {campaign?.name}
                </Text>
                {campaign?.platform === "1" ? (
                  <FcAndroidOs size={24} />
                ) : (
                  <FaApple size={24} />
                )}
              </Flex>
              <Flex gap={2}>
                <Text color="gray.700" fontWeight={"normal"} fontSize="lg">
                  {campaign?.title}
                </Text>
                {(creatives?.iconUrl || campaign?.iconUrl) && (
                  <Image
                    src={creatives?.iconUrl ?? campaign?.iconUrl}
                    boxSize={6}
                  />
                )}
              </Flex>
              <Flex my={4}>
                <HSeparator />
              </Flex>
              <Flex gap={1}>
                <Text color="gray.700" fontWeight={"semibold"}>
                  Countries:
                </Text>
                <Text color="gray.700" fontWeight={"normal"}>
                  {campaign?.countries
                    ? Array.isArray(campaign?.countries) &&
                      campaign?.countries.length > 0
                      ? campaign?.countries.join(", ")
                      : "Global"
                    : "Global"}
                </Text>
              </Flex>
              {campaign.minApiLevel && campaign.maxApiLevel && (
                <Flex gap={1}>
                  <Text color="gray.700" fontWeight={"semibold"}>
                    OS Levels:
                  </Text>
                  <Text color="gray.700" fontWeight={"normal"}>
                    {`${platform} ${campaign?.minApiLevel} - ${campaign?.maxApiLevel}`}
                  </Text>
                </Flex>
              )}
              {campaign?.minAge && campaign?.maxAge && (
                <Flex gap={1}>
                  <Text color="gray.700" fontWeight={"semibold"}>
                    Ages:
                  </Text>
                  <Text color="gray.700" fontWeight={"normal"}>
                    {campaign?.minAge} - {campaign?.maxAge}
                  </Text>
                </Flex>
              )}
              {campaign?.genders &&
                Array.isArray(campaign.genders) &&
                campaign.genders.length > 0 && (
                  <Flex gap={1}>
                    <Text color="gray.700" fontWeight={"semibold"}>
                      Genders:
                    </Text>
                    <Text color="gray.700" fontWeight={"normal"}>
                      {campaign?.genders
                        ? campaign?.genders
                            .map((g) =>
                              g === 0 ? "Male" : g === 1 ? "Female" : "Other"
                            )
                            .join(", ")
                        : "-"}
                    </Text>
                  </Flex>
                )}
              <Flex my={4}>
                <HSeparator />
              </Flex>
              <Flex>
                <Flex flex={2} flexDir="column">
                  <Flex gap={1}>
                    <Text color="gray.700" fontWeight={"semibold"}>
                      Daily Budget Cap:
                    </Text>
                    <Text color="gray.700" fontWeight={"normal"}>
                      {campaign.dailyBudgetCap
                        ? `$${NumberWithCommas(campaign?.dailyBudgetCap)}`
                        : "-"}
                    </Text>
                  </Flex>
                  <Flex gap={1}>
                    <Text color="gray.700" fontWeight={"semibold"}>
                      Bidding Type:
                    </Text>
                    <Text color="gray.700" fontWeight={"normal"}>
                      ROAS
                    </Text>
                  </Flex>
                </Flex>
                <Flex flex={1} flexDir="column">
                  {Object.keys(roasGoals)
                    .map((key) => ({
                      dx: key,
                      value: roasGoals[key as keyof RoasGoal],
                    }))
                    .filter((goal) => goal.value)
                    .map((key) => {
                      return (
                        <Flex key={key.dx} gap={1}>
                          <Text color="gray.700" fontWeight={"semibold"}>
                            {key.dx}:
                          </Text>
                          <Text color="gray.700" fontWeight={"normal"}>
                            {key.value}%
                          </Text>
                        </Flex>
                      );
                    })}
                </Flex>
              </Flex>
              <Flex my={4}>
                <HSeparator />
              </Flex>
              <Flex gap={1} flexDir="column" mr={4}>
                <Text color="gray.700" fontWeight={"semibold"}>
                  Tracking Link:
                </Text>
                <Text 
                  color="gary.700" 
                  fontWeight={"normal"} 
                  fontSize="md"
                  wordBreak="break-all"
                  overflowWrap="break-word"
                >
                  {campaign?.clickUrl}
                </Text>
              </Flex>
            </Flex>
          </Flex>
          <Flex flexDir={"column"} gap={2} flex={1}>
            <Flex
              flexDir={"column"}
              gap={1}
              bgColor="gray.50"
              p={4}
              borderRadius={"lg"}
              boxShadow={"sm"}
            >
              <Flex flex={1} justifyContent={"center"}>
                <Flex
                  bgColor="white"
                  boxShadow="sm"
                  borderWidth={1}
                  borderColor="gray.200"
                  borderRadius="2xl"
                  flexDir="column"
                  width={450}
                  height={420}
                  gap={8}
                >
                  <Flex
                    as={motion.div}
                    initial={{ scale: 1 }}
                    animate={{
                      scale:
                        creativeFiles.bannerImage || creatives.bannerImageUrl
                          ? 1.08
                          : 1,
                    }}
                    transition="0.5s ease 0.5s"
                    borderRadius="2xl"
                    width="100%"
                    height={200}
                    bgColor={
                      creativeFiles.bannerImage || creatives.bannerImageUrl
                        ? "transparent"
                        : "gray.200"
                    }
                    pos="relative"
                    boxShadow={
                      creativeFiles.bannerImage || creatives.bannerImageUrl
                        ? `0px 8px 24px ${hexToRGB("#422AFB", 0.25)}`
                        : "none"
                    }
                  >
                    {creativeFiles.bannerImage || creatives.bannerImageUrl ? (
                      <>
                        {(creativeFiles.bannerVideo ||
                          creatives.bannerVideoUrl) && (
                          <Flex
                            pos={"absolute"}
                            w="100%"
                            h={"100%"}
                            alignItems="center"
                            justifyContent={"center"}
                            bgColor="blackAlpha.200"
                            _hover={{
                              bgColor: "blackAlpha.600",
                              cursor: "pointer",
                            }}
                            borderRadius="2xl"
                            transition={"all 0.5s ease"}
                            onClick={handlePlayVideo}
                          >
                            <Flex
                              width={32}
                              height={32}
                              borderRadius="full"
                              alignItems="center"
                              justify={"center"}
                            >
                              <FaPlay size={64} color="white" />
                            </Flex>
                          </Flex>
                        )}
                        <Image
                          src={
                            creativeFiles.bannerImage
                              ? URL.createObjectURL(creativeFiles.bannerImage)
                              : creatives.bannerImageUrl
                          }
                          borderRadius="2xl"
                          width="100%"
                          height="100%"
                          objectFit="cover"
                        />
                      </>
                    ) : (
                      <Text
                        flex={1}
                        alignSelf={"center"}
                        justifySelf="center"
                        textAlign={"center"}
                        fontWeight="medium"
                        color="gray.600"
                      >
                        Upload a banner image to see the preview here.
                      </Text>
                    )}
                  </Flex>
                  <Flex
                    flex={1}
                    flexDir="column"
                    px={6}
                    gap={8}
                    justifyContent="space-between"
                    pb={8}
                  >
                    <Flex justifyContent="space-between">
                      <Flex flexDir="column" flex={2}>
                        <Heading noOfLines={1} fontSize="2xl">
                          {campaign?.title}
                        </Heading>
                        <Text fontSize="lg" color="gray.600">
                          New for you!
                        </Text>
                      </Flex>
                      <Flex flexDir="column" alignItems="flex-end" flex={1}>
                        <Flex alignItems="center" gap={1}>
                          <Text fontSize="2xl" fontWeight="bold">
                            +1090
                          </Text>
                          <Lottie
                            options={{
                              loop: true,
                              autoplay: true,
                              animationData: animationData,
                              rendererSettings: {
                                preserveAspectRatio: "xMidYMid slice",
                              },
                            }}
                            speed={0.4}
                            height={28}
                            width={28}
                          />
                          {/* <Image
                          src={require("../../../../../assets/img/dollar.png")}
                          boxSize={8}
                        /> */}
                        </Flex>
                        <Text
                          fontSize="md"
                          color="gray.600"
                          mt={-1}
                          textAlign="center"
                        >
                          Coins per minute
                        </Text>
                      </Flex>
                    </Flex>
                    <Button
                      _hover={{}}
                      flexDir="row"
                      textTransform="uppercase"
                      cursor="pointer"
                      as={motion.div}
                      width="100%"
                      px={4}
                      py={6}
                      bgGradient={`linear(to-tr, ${GRADIENT_BUTTON_1}, ${GRADIENT_BUTTON_2}, ${GRADIENT_BUTTON_3})`}
                      fontSize="2xl"
                      color="white"
                      fontWeight="bold"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="full"
                      textShadow={`1px 1px ${hexToRGB(PURPLE, 0.8)}`}
                      boxShadow={`${hexToRGB(
                        GRADIENT_BUTTON_2,
                        0.5
                      )} 0px 8px 24px`}
                      gap={4}
                    >
                      <Text>Play & Earn</Text>
                      {creatives?.iconUrl && (
                        <Image
                          src={creatives?.iconUrl}
                          boxSize={12}
                          borderRadius="full"
                        />
                      )}
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {!campaignId && (
          <GamelightButton
            alignSelf={"center"}
            size="lg"
            w={"100%"}
            mt={6}
            onClick={onSubmit}
            isLoading={isSubmitting}
            disabled={isSubmitting || (!isCampaignCreate && !canEditCampaign)}
            loadingText="Processing"
          >
            Launch Campaign
          </GamelightButton>
        )}

        <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalCloseButton />
            </ModalHeader>
            <ModalBody p={8}>
              <video
                ref={videoRef}
                src={
                  creatives.bannerVideoUrl ??
                  (creativeFiles.bannerVideo &&
                    URL.createObjectURL(creativeFiles.bannerVideo)) ??
                  ""
                }
                style={{ width: "100%", borderRadius: "0.5rem" }}
                controls
                autoPlay
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Flex>
    </Flex>
  );
};

export default Preview;
